import { formatCompactDateTime } from '@/utils/dateFormatters'
import { getInitials } from '@/utils/stringFormatters'
import { ClockCircleOutlined } from '@ant-design/icons'
import { BBDTag, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Avatar, Popover, Typography } from 'antd'
import { type CSSProperties, type ReactNode, useState } from 'react'

const { Text } = Typography

export interface UpdatedByTagProps {
  createdBy: string
  createdDate: string | Date
  updatedBy?: string
  updatedDate?: string | Date
  onClick?: () => void
  title?: string
  viewHistoryLabel?: string
  style?: CSSProperties
  className?: string
}

interface UserRowProps {
  label: string
  userName: string
  date: string | Date
}

function UserRow({ label, userName, date }: UserRowProps) {
  return (
    <Horizontal alignItems='flex-start' gap={10} style={{ padding: '8px 0' }}>
      <Avatar
        size={28}
        style={{
          backgroundColor: 'var(--gray-400)',
          flexShrink: 0,
        }}
      >
        {getInitials(userName)}
      </Avatar>
      <Vertical style={{ minWidth: 0 }}>
        <Text
          strong
          style={{
            color: 'var(--foreground)',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          {label}
        </Text>
        <Horizontal alignItems='baseline' gap={16} style={{ marginTop: 2 }}>
          <Text style={{ color: 'var(--foreground)', fontSize: 13 }}>
            {userName}
          </Text>
          <Text type='secondary' style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            At: {formatCompactDateTime(date)}
          </Text>
        </Horizontal>
      </Vertical>
    </Horizontal>
  )
}

interface PopoverContentProps {
  createdBy: string
  createdDate: string | Date
  updatedBy?: string
  updatedDate?: string | Date
  title?: string
  viewHistoryLabel?: string
  onClick?: () => void
}

function PopoverContent({
  createdBy,
  createdDate,
  updatedBy,
  updatedDate,
  title = 'Order History',
  viewHistoryLabel = 'View History',
  onClick,
}: PopoverContentProps) {
  return (
    <Vertical style={{ minWidth: 260, maxWidth: 320 }}>
      <Horizontal
        alignItems='center'
        justifyContent='space-between'
        style={{
          paddingBottom: 8,
          borderBottom: '1px solid var(--border)',
          marginBottom: 4,
        }}
      >
        <Horizontal alignItems='center' gap={8}>
          <ClockCircleOutlined
            style={{ fontSize: 14, color: 'var(--gray-500)' }}
          />
          <Text strong style={{ fontSize: 14, color: 'var(--foreground)' }}>
            {title}
          </Text>
        </Horizontal>
        {onClick && (
          <span
            className='cursor-pointer'
            onClick={(e) => {
              e.stopPropagation()
              onClick()
            }}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation()
                onClick()
              }
            }}
          >
            <Texto style={{ fontSize: 12 }}>{viewHistoryLabel}</Texto>
          </span>
        )}
      </Horizontal>
      <UserRow label='Created By' userName={createdBy} date={createdDate} />
      {updatedBy && updatedDate && (
        <UserRow
          label='Last Updated By'
          userName={updatedBy}
          date={updatedDate}
        />
      )}
    </Vertical>
  )
}

/**
 * A reusable component that displays created/updated information in a compact tag
 * with a popover showing full details. Exposes an onClick prop for viewing history.
 */
export function UpdatedByTag({
  createdBy,
  createdDate,
  updatedBy,
  updatedDate,
  onClick,
  title,
  viewHistoryLabel,
  style,
  className,
}: UpdatedByTagProps) {
  const [open, setOpen] = useState(false)

  // Show most recent user/date in the compact tag
  const displayUser = updatedBy ?? createdBy
  const displayDate = updatedDate ?? createdDate

  const handleClick = () => {
    setOpen(false)
    onClick?.()
  }

  const trigger: ReactNode = (
    <BBDTag
      className={`${className ?? 'p-1'} ${onClick ? 'cursor-pointer' : ''}`}
      style={style}
      onClick={handleClick}
    >
      <Horizontal alignItems='center' gap={8}>
        <Avatar
          size={14}
          style={{
            backgroundColor: 'var(--gray-400)',
            fontSize: 10,
            flexShrink: 0,
          }}
        >
          {getInitials(displayUser)}
        </Avatar>
        <Vertical style={{ minWidth: 0, flex: 1 }}>
          <Texto
            style={{
              fontSize: 11,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatCompactDateTime(displayDate)}
          </Texto>
        </Vertical>
      </Horizontal>
    </BBDTag>
  )

  return (
    <Popover
      content={
        <PopoverContent
          createdBy={createdBy}
          createdDate={createdDate}
          updatedBy={updatedBy}
          updatedDate={updatedDate}
          title={title}
          viewHistoryLabel={viewHistoryLabel}
          onClick={handleClick}
        />
      }
      trigger='hover'
      placement='bottomLeft'
      arrow={false}
      open={open}
      onOpenChange={setOpen}
    >
      {trigger}
    </Popover>
  )
}
